// What people answered, drawn for the person who asked the questions.
//
// EVERY NUMBER ON THIS SCREEN WAS CALCULATED BY xp/operator AND NONE OF IT HERE.
// The server decides which field_ids are survey_question, how each format is
// counted, and that free text is a count rather than a quotation. Recomputing
// any of that here would put a second definition of a vote in the estate, and
// the two would diverge the first time somebody treated a job title as one.
//
// EMPTY IS A SENTENCE, NOT A CHART. A list of questions at zero looks like a
// survey that ran. This show has not, and the page says so.
//
// FREE TEXT IS NOT DRAWN HERE. The server does not send the words to a
// dashboard, and a screen that invented a place for them would be the
// disclosure the report withholds. Since 12 Oct (decision C6) they can be
// exported, from Data, under the purpose named for them, and the Free text
// section below says so in place of "counted and never produced".
//
// ONE DIVISION IS DONE HERE, AND IT IS THE DRAWING. The design draws each
// answer as a bar with its share beside it (TASKS §143, step 5). That share is
// an option's answers over the people who answered its question, both of them
// the server's numbers, so it is a picture of the count and not a second
// definition of it. For a question that takes several answers the bars do not
// add up to a whole, because one person can be in more than one of them.

import { useEffect, useState, type ReactNode } from "react";
import type {
  SurveyQuestionCounts,
  SurveyReport,
} from "../../../operator/src/survey.js";
import { PageHead } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import { Empty, Loading } from "@nova/desk-ui/states";
import { readReport, type ApiOptions, type Failure } from "../api/client.js";
import { count, moment, share } from "../operator/present.js";
import { ReadFailed } from "./ReadFailed.js";
import { useShow } from "./Show.js";
import type { SessionToken } from "../session/store.js";

export type SurveyProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type State =
  | { readonly phase: "reading" }
  | { readonly phase: "read"; readonly report: SurveyReport }
  | { readonly phase: "failed"; readonly failure: Failure };

export function Survey({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: SurveyProps): ReactNode {
  const show = useShow();
  const [state, setState] = useState<State>({ phase: "reading" });
  // Bumped by "Ask again"; the effect below reads again whenever it changes.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (show.status !== "ready") return;
    let cancelled = false;
    setState({ phase: "reading" });

    void (async () => {
      const result = await readReport<SurveyReport>(api, token, "survey", activationId);
      if (cancelled) return;

      if (result.ok) {
        setState({ phase: "read", report: result.value });
        return;
      }

      setState({ phase: "failed", failure: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, show.status, attempt]);

  // The head is on the page whatever the read did, as the design draws it.
  const head = (note?: string) => (
    <PageHead
      title="Survey answers"
      lede="Grouped by question, over the people who were asked. A question nobody answered is shown with its zero rather than dropped, because a question that failed to land is worth knowing about."
      uuid={activationId}
      {...(note === undefined ? {} : { note })}
      explain="info"
    />
  );
  // "Ask again" asks again for whatever failed: the show's own read, which the
  // report waits on, or the report itself.
  const retry = () => (show.status === "failed" ? show.reload() : setAttempt((n) => n + 1));
  const failure = show.status === "failed" ? show.failure : state.phase === "failed" ? state.failure : null;
  if (failure !== null) {
    return (
      <div className="stack">
        {head()}
        <ReadFailed
          what="Could not load the survey report"
          failure={failure}
          role={role}
          onEnrol={onEnrol}
          notCounted="No survey figures are shown because the report did not load."
          onRetry={retry}
        />
      </div>
    );
  }
  if (show.status !== "ready" || state.phase !== "read") {
    return (
      <div className="stack">
        {head()}
        <Loading rows={3} cols={3} label="Reading survey answers…" />
      </div>
    );
  }

  const report = state.report;
  const questions = report.questions ?? [];
  const completions = report.completions ?? 0;

  return (
    <div className="stack">
      {head()}

      <section>
        <h2 className="sec">About these counts</h2>
        <div className="panel panel-stack">
          <p style={{ margin: 0 }}>{report.counted_note ?? ""}</p>
          {report.scope_note ? <p className="help help-tight">{report.scope_note}</p> : null}
          {/* When it was read and under whose access, with what was counted
              rather than in the head, which the design keeps to the page. */}
          <p className="help help-tight">
            Read at {moment(report.as_at)}. {report.access_note ?? "Access details are unavailable."}
          </p>
          {/* The people each question below is over is in its own line; the
              people who answered none of them are only here. */}
          {(report.survey_skipped ?? 0) > 0 ? (
            <p className="help help-tight">
              {count(report.survey_skipped ?? 0)} of the {count(completions)}{" "}
              {completions === 1 ? "person" : "people"} who completed registration skipped every survey question.
            </p>
          ) : null}
        </div>
      </section>

      <h2 className="sec" style={{ marginBottom: -8 }}>Answers by question</h2>
      {report.empty === true || questions.length === 0 ? (
        <Empty title="Nobody has answered anything">
          <p>This show has no survey answers yet.</p>
          {report.empty_note ? <p>{report.empty_note}</p> : null}
        </Empty>
      ) : (
        questions.map((question) => (
          <Question key={question.field_id} question={question} completions={completions} />
        ))
      )}

      <FreeText questions={questions} />
    </div>
  );
}

/**
 * Where the written answers are, as the prototype draws it (console-dash.jsx,
 * Free text): not on this screen, and producible from Data under a named
 * purpose. The count is the server's.
 */
function FreeText({ questions }: { questions: readonly SurveyQuestionCounts[] }): ReactNode {
  const written = questions.filter((question) => question.format === "free_text");
  const answers = written.reduce((sum, question) => sum + question.answered, 0);
  return (
    <section>
      <SecLabel>Free text</SecLabel>
      <div className="panel panel-stack">
        <p style={{ margin: 0 }}>
          {written.length === 0
            ? "This show asked no free-text question. "
            : `${count(answers)} written ${answers === 1 ? "answer" : "answers"} to ${count(written.length)} free-text ${written.length === 1 ? "question" : "questions"}. `}
          A free-text answer is personal data whatever it says, so it is not read on a dashboard. It can be
          exported from the Data page under the purpose &ldquo;Written survey answers&rdquo;, for the people who
          agreed to their details being shared with the client, and every export is recorded.
        </p>
      </div>
    </section>
  );
}

function Question({
  question,
  completions,
}: {
  question: SurveyQuestionCounts;
  completions: number;
}): ReactNode {
  return (
    <section>
      <SecLabel>{question.question}</SecLabel>
      <div className="panel panel-stack">
        <p className="help" style={{ margin: 0 }}>
          {FORMAT[question.format] ?? question.format}. {count(question.answered)} of {count(completions)} answered (
          {share(question.answered_rate)}); the {count(question.skipped)} who did not ({share(question.skipped_rate)}) are
          not in the shares below.
        </p>
        {question.format === "single_choice" || question.format === "multi_choice" ? (
          <Bars
            answered={question.answered}
            rows={question.options.map((option) => ({
              key: option.option_id,
              label: option.label ?? option.option_id,
              answers: option.answers,
            }))}
          />
        ) : null}
        {question.format === "rating_scale" ? (
          <Bars
            answered={question.answered}
            rows={question.points.map((point) => ({
              key: String(point.rating),
              label: `Rated ${count(point.rating)}`,
              answers: point.answers,
            }))}
          />
        ) : null}
      </div>
    </section>
  );
}

/** The design's bars: a label, the share drawn, and the count with its share. */
function Bars({
  answered,
  rows,
}: {
  answered: number;
  rows: { key: string; label: string; answers: number }[];
}): ReactNode {
  return (
    <div className="sbars">
      {rows.map((row) => {
        // Nobody answered: no share to draw, and nought rather than a division by it.
        const part = answered > 0 ? row.answers / answered : 0;
        return (
          <div className="sbar" key={row.key}>
            <span className="sbar-l">{row.label}</span>
            <span className="sbar-t">
              <i style={{ width: `${Math.round(part * 100)}%` }}></i>
            </span>
            <span className="sbar-n">
              {count(row.answers)} <span className="quiet">{share(part)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** What kind of question it was, in words, where the old screen printed the format's id. */
const FORMAT: Record<string, string> = {
  single_choice: "One answer each",
  multi_choice: "Any number of answers each",
  rating_scale: "A rating",
  free_text: "Written answers",
};
